import { unwrap, type CountryOut } from "@sagot/api-client";
import { COUNTRIES_GC_TIME_MS, HOME_COUNTRY } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import type { ComboboxOption } from "@sagot/ui";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useApi } from "../session/session-provider";
import { queryKeys } from "./query-keys";

export type CountryEntry = CountryOut;

const FALLBACK_TIMEZONE = "UTC";

/**
 * `GET /v1/meta/countries` — every ISO 3166-1 country with its IANA zones and billing-currency
 * hint (DEC-054). Static reference data: fetched once per session, kept for a day.
 */
export function useCountries() {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.countries,
    queryFn: () => unwrap(api.GET("/v1/meta/countries")),
    staleTime: Infinity,
    gcTime: COUNTRIES_GC_TIME_MS,
  });
}

/** Catalogue entry of a country code, if the list is loaded and knows it. */
export function findCountry(countries: readonly CountryEntry[] | undefined, code: string | null | undefined): CountryEntry | undefined {
  return countries?.find((country) => country.code === code);
}

function regionNames(language: string): Intl.DisplayNames | null {
  try {
    return new Intl.DisplayNames([language], { type: "region" });
  } catch {
    return null;
  }
}

/**
 * Picker options: names in the UI language (`Intl.DisplayNames`, the API's English name as
 * fallback) sorted by that name, the home country first, and every `keep` code (the chosen or
 * stored value) kept selectable even when the catalogue does not know it (a legacy stored value).
 */
export function countryOptions(countries: readonly CountryEntry[], language: string, keep: readonly (string | null | undefined)[] = []): ComboboxOption[] {
  const names = regionNames(language);
  const collator = new Intl.Collator(language);
  const options = countries.map((country) => {
    const localized = names?.of(country.code);
    const label = localized && localized !== country.code ? localized : country.name;
    return { value: country.code, label, keywords: [country.code, country.name] };
  });
  options.sort((a, b) => collator.compare(a.label, b.label));
  const home = options.findIndex((option) => option.value === HOME_COUNTRY);
  const [homeOption] = home >= 0 ? options.splice(home, 1) : [];
  if (homeOption) options.unshift(homeOption);
  for (const code of keep) {
    if (code && !options.some((option) => option.value === code)) options.unshift({ value: code, label: code, keywords: [] });
  }
  return options;
}

/** Country options in the current UI language (`en` or `fil`); `current` / `saved` stay selectable. */
export function useCountryOptions(countries: readonly CountryEntry[] | undefined, current?: string | null, saved?: string | null): ComboboxOption[] {
  const { i18n } = useTranslation();
  const language = i18n.language === "fil" ? "fil" : "en";
  return useMemo(() => countryOptions(countries ?? [], language, [current, saved]), [countries, language, current, saved]);
}

/** Display name of one country in the UI language; the code itself when unknown. */
export function useCountryLabel(countries: readonly CountryEntry[] | undefined): (code: string) => string {
  const options = useCountryOptions(countries);
  return useMemo(() => {
    const labels = new Map(options.map((option) => [option.value, option.label]));
    return (code: string) => labels.get(code) ?? code;
  }, [options]);
}

function browserTimezones(): readonly string[] | null {
  try {
    const zones = Intl.supportedValuesOf("timeZone");
    return zones.length > 0 ? zones : null;
  } catch {
    return null;
  }
}

/**
 * Every zone the browser knows (`Intl.supportedValuesOf`); the union of the API's per-country
 * zones when it does not support that call. Always offers UTC.
 */
export function allTimezones(countries: readonly CountryEntry[] | undefined): readonly string[] {
  const zones = new Set(browserTimezones() ?? (countries ?? []).flatMap((country) => country.timezones));
  zones.add(FALLBACK_TIMEZONE);
  return [...zones].sort();
}

export interface TimezoneChoices {
  /** The stored zone when neither list knows it (stays selectable). */
  saved: string | null;
  /** The country's own zones, in API order. */
  country: readonly string[];
  /** Every other zone. */
  others: readonly string[];
}

/** Zones to offer: the country's first, then all the rest, keeping `current` selectable. */
export function timezoneChoices(country: CountryEntry | undefined, all: readonly string[], current: string): TimezoneChoices {
  const own = country?.timezones ?? [];
  const ownSet = new Set(own);
  const others = all.filter((zone) => !ownSet.has(zone));
  const known = ownSet.has(current) || all.includes(current);
  return { saved: current && !known ? current : null, country: own, others };
}

/** Zone to preselect for a country: the visitor's own when it belongs there, else the first. */
export function pickTimezone(country: CountryEntry | undefined, localZone: string | undefined): string | null {
  const zones = country?.timezones ?? [];
  if (zones.length === 0) return null;
  if (localZone && zones.includes(localZone)) return localZone;
  return zones[0] ?? null;
}
